import { create } from 'zustand'
import {
  collection, doc, addDoc, setDoc, getDoc, getDocs, updateDoc, writeBatch,
  query, where, orderBy, limit, startAfter, serverTimestamp, Timestamp, arrayUnion, onSnapshot,
  type QueryDocumentSnapshot, type DocumentData,
} from 'firebase/firestore'
import { db, auth } from '../firebase'

const MESSAGES_PAGE_SIZE = 30

export type MessageTopic = 'schedule' | 'progress' | 'concern' | 'payment' | 'general'

export interface MessageAttachment {
  filename: string
  mimeType: string
  sizeBytes: number
  storagePath: string
}

export interface Message {
  id: string
  senderUid: string
  senderName: string
  body: string
  topic: MessageTopic
  attachments: MessageAttachment[]
  readBy: string[]
  deletedFor: string[]
  createdAt: Timestamp | null
}

export interface Conversation {
  id: string
  participantUids: string[]
  participantNames: Record<string, string>
  participantPhotos: Record<string, string | null>
  topic: MessageTopic
  profileId?: string
  profileName?: string
  orgName?: string
  lastMessage: string
  lastMessageAt: Timestamp | null
  lastSenderUid: string
  lastReadAt: Record<string, Timestamp>
  archivedFor: string[]
  keepForever: boolean
  expiresAt: Timestamp | null
  createdAt: Timestamp | null
}

// Module-level unsub refs — survive Zustand store resets
let _inboxUnsub: (() => void) | null = null
let _threadUnsub: (() => void) | null = null
let _subscribedUid: string | null = null

// Thread pagination — the live listener only watches the most recent MESSAGES_PAGE_SIZE
// messages; older pages loaded via loadOlderMessages are held here and merged in.
let _earlierMessages: Message[] = []
let _liveMessages: Message[] = []
let _paginationCursor: QueryDocumentSnapshot<DocumentData> | null = null

function mergeMessages(): Message[] {
  const seen = new Set<string>()
  const out: Message[] = []
  for (const m of [..._earlierMessages, ..._liveMessages]) {
    if (seen.has(m.id)) continue
    seen.add(m.id)
    out.push(m)
  }
  return out
}

interface MessagingState {
  conversations: Conversation[]
  messages: Message[]
  inboxLoading: boolean
  threadLoading: boolean
  hasMoreMessages: boolean
  loadingMoreMessages: boolean
  activeConversationId: string | null
  subscribeInbox: (uid: string) => void
  subscribeThread: (conversationId: string) => void
  loadOlderMessages: (conversationId: string) => Promise<void>
  unsubscribeThread: () => void
  unsubscribeAll: () => void
  getOrCreateConversation: (
    otherUid: string,
    otherName: string,
    topic: MessageTopic,
    profileId?: string,
    profileName?: string,
    orgName?: string,
  ) => Promise<string>
  sendMessage: (conversationId: string, body: string, attachments?: MessageAttachment[]) => Promise<void>
  markThreadRead: (conversationId: string) => Promise<void>
  softDeleteMessage: (conversationId: string, messageId: string) => Promise<void>
  archiveConversation: (conversationId: string) => Promise<void>
  keepThread: (conversationId: string) => Promise<void>
  downloadThread: (conversationId: string) => void
}

// Track last-refreshed timestamp per uid to avoid re-running on every snapshot
const _lastPhotoRefresh: Record<string, number> = {}

async function refreshParticipantPhotos(uid: string, conversations: Conversation[]) {
  const now = Date.now()
  // Debounce: only re-fetch once per 5 minutes per uid
  if (_lastPhotoRefresh[uid] && now - _lastPhotoRefresh[uid] < 5 * 60 * 1000) return
  _lastPhotoRefresh[uid] = now

  // Collect all unique participant uids across all conversations
  const allUids = new Set<string>()
  for (const conv of conversations) {
    for (const p of conv.participantUids) allUids.add(p)
  }
  allUids.delete(uid) // current user — use auth directly

  // Fetch current photoURL from Firestore for each other participant
  const photoMap: Record<string, string | null> = {}
  try {
    await Promise.all(Array.from(allUids).map(async (otherUid) => {
      try {
        const snap = await getDoc(doc(db, 'users', otherUid))
        photoMap[otherUid] = (snap.data()?.photoURL as string | undefined) ?? null
      } catch {
        // non-fatal
      }
    }))
  } catch {
    return
  }

  // Also include current user's own photo from Firebase Auth
  const currentPhotoURL = auth.currentUser?.photoURL ?? null
  photoMap[uid] = currentPhotoURL

  // Batch-patch any conversation where a stored photo is stale
  const batch = writeBatch(db)
  let batchCount = 0
  for (const conv of conversations) {
    const updates: Record<string, string | null> = {}
    for (const participantUid of conv.participantUids) {
      if (participantUid in photoMap) {
        const current = conv.participantPhotos?.[participantUid] ?? null
        const fresh = photoMap[participantUid]
        if (current !== fresh) updates[`participantPhotos.${participantUid}`] = fresh
      }
    }
    if (Object.keys(updates).length > 0) {
      batch.update(doc(db, 'conversations', conv.id), updates)
      batchCount++
    }
  }
  if (batchCount > 0) {
    try {
      await batch.commit()
    } catch (err) {
      console.warn('[messaging] refreshParticipantPhotos batch failed:', err)
    }
  }
}

function currentUser() {
  const user = auth.currentUser
  if (!user) throw new Error('Not authenticated')
  return user
}

function makeConversationId(uid1: string, uid2: string, topic: MessageTopic, profileId?: string): string {
  const base = `${[uid1, uid2].sort().join('_')}_${topic}`
  return profileId ? `${base}_${profileId}` : base
}

export const useMessagingStore = create<MessagingState>((set, get) => ({
  conversations: [],
  messages: [],
  inboxLoading: false,
  threadLoading: false,
  hasMoreMessages: false,
  loadingMoreMessages: false,
  activeConversationId: null,

  subscribeInbox: (uid: string) => {
    if (_subscribedUid === uid && _inboxUnsub) return
    _inboxUnsub?.()
    _subscribedUid = uid
    set({ inboxLoading: true })
    _inboxUnsub = onSnapshot(
      query(collection(db, 'conversations'), where('participantUids', 'array-contains', uid)),
      (snap) => {
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) } as Conversation))
        const filtered = raw
          .filter((c) => !(c.archivedFor ?? []).includes(uid))
          .sort((a, b) => {
            const aTime = a.lastMessageAt?.toMillis() ?? a.createdAt?.toMillis() ?? 0
            const bTime = b.lastMessageAt?.toMillis() ?? b.createdAt?.toMillis() ?? 0
            return bTime - aTime
          })
        set({ conversations: filtered, inboxLoading: false })
        // Background: refresh stale participantPhotos for all loaded convs
        void refreshParticipantPhotos(uid, filtered)
      },
      (err) => {
        console.warn('[messaging] inbox snapshot error:', err)
        set({ inboxLoading: false })
      },
    )
  },

  subscribeThread: (conversationId: string) => {
    _threadUnsub?.()
    _earlierMessages = []
    _liveMessages = []
    _paginationCursor = null
    set({
      threadLoading: true, messages: [], activeConversationId: conversationId,
      hasMoreMessages: false, loadingMoreMessages: false,
    })
    const messagesRef = collection(db, 'conversations', conversationId, 'messages')
    _threadUnsub = onSnapshot(
      query(messagesRef, orderBy('createdAt', 'desc'), limit(MESSAGES_PAGE_SIZE)),
      (snap) => {
        _liveMessages = snap.docs
          .map((d) => ({ id: d.id, ...d.data() } as Message))
          .sort((a, b) => (a.createdAt?.toMillis() ?? 0) - (b.createdAt?.toMillis() ?? 0))
        // Only the first snapshot (before any pagination) sets the "load older" cursor —
        // once the user has paginated back, later live updates shouldn't reset it.
        const firstSnapshot = _earlierMessages.length === 0
        if (firstSnapshot && snap.docs.length > 0) {
          _paginationCursor = snap.docs[snap.docs.length - 1]!
        }
        set({
          messages: mergeMessages(),
          threadLoading: false,
          hasMoreMessages: firstSnapshot ? snap.docs.length === MESSAGES_PAGE_SIZE : get().hasMoreMessages,
        })
      },
      (err) => {
        console.warn('[messaging] thread snapshot error:', err)
        set({ threadLoading: false })
      },
    )
  },

  loadOlderMessages: async (conversationId: string) => {
    const { loadingMoreMessages, hasMoreMessages } = get()
    if (loadingMoreMessages || !hasMoreMessages || !_paginationCursor) return
    set({ loadingMoreMessages: true })
    try {
      const messagesRef = collection(db, 'conversations', conversationId, 'messages')
      const snap = await getDocs(
        query(messagesRef, orderBy('createdAt', 'desc'), startAfter(_paginationCursor), limit(MESSAGES_PAGE_SIZE)),
      )
      const page = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as Message))
        .sort((a, b) => (a.createdAt?.toMillis() ?? 0) - (b.createdAt?.toMillis() ?? 0))
      _earlierMessages = [...page, ..._earlierMessages]
      if (snap.docs.length > 0) _paginationCursor = snap.docs[snap.docs.length - 1]!
      set({
        messages: mergeMessages(),
        hasMoreMessages: snap.docs.length === MESSAGES_PAGE_SIZE,
        loadingMoreMessages: false,
      })
    } catch (err: unknown) {
      console.warn('[messaging] loadOlderMessages failed:', err)
      set({ loadingMoreMessages: false })
    }
  },

  unsubscribeThread: () => {
    _threadUnsub?.()
    _threadUnsub = null
    _earlierMessages = []
    _liveMessages = []
    _paginationCursor = null
    set({ messages: [], threadLoading: false, activeConversationId: null, hasMoreMessages: false, loadingMoreMessages: false })
  },

  unsubscribeAll: () => {
    _inboxUnsub?.()
    _threadUnsub?.()
    _inboxUnsub = null
    _threadUnsub = null
    _subscribedUid = null
    _earlierMessages = []
    _liveMessages = []
    _paginationCursor = null
    set({
      conversations: [], messages: [], inboxLoading: false, threadLoading: false,
      activeConversationId: null, hasMoreMessages: false, loadingMoreMessages: false,
    })
  },

  getOrCreateConversation: async (otherUid, otherName, topic, profileId, profileName, orgName) => {
    const user = currentUser()
    const conversationId = makeConversationId(user.uid, otherUid, topic, profileId)
    const convRef = doc(db, 'conversations', conversationId)
    const existing = await getDoc(convRef)
    if (!existing.exists()) {
      let otherPhotoURL: string | null = null
      try {
        const otherSnap = await getDoc(doc(db, 'users', otherUid))
        otherPhotoURL = (otherSnap.data()?.photoURL as string | undefined) ?? null
      } catch (err: unknown) {
        console.warn('[messaging.getOrCreateConversation] non-fatal:', err)
      }
      await setDoc(convRef, {
        participantUids: [user.uid, otherUid].sort(),
        participantNames: {
          [user.uid]: user.displayName || user.email || 'Me',
          [otherUid]: otherName,
        },
        participantPhotos: {
          [user.uid]: user.photoURL ?? null,
          [otherUid]: otherPhotoURL,
        },
        topic,
        ...(profileId ? { profileId, profileName: profileName ?? '' } : {}),
        ...(orgName ? { orgName } : {}),
        lastMessage: '',
        lastMessageAt: null,
        lastSenderUid: '',
        lastReadAt: {},
        archivedFor: [],
        keepForever: false,
        expiresAt: Timestamp.fromMillis(Date.now() + 365 * 24 * 60 * 60 * 1000),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    } else {
      const existingData = existing.data()!
      if ((existingData.archivedFor as string[] ?? []).includes(user.uid)) {
        await updateDoc(convRef, {
          archivedFor: (existingData.archivedFor as string[]).filter((u) => u !== user.uid),
        })
      }
      if (orgName && !existingData.orgName) await updateDoc(convRef, { orgName })
      if (profileId && !existingData.profileId) {
        await updateDoc(convRef, { profileId, profileName: profileName ?? '' })
      }
    }
    return conversationId
  },

  sendMessage: async (conversationId, body, attachments = []) => {
    const user = currentUser()
    const convRef = doc(db, 'conversations', conversationId)
    const activeConv = get().conversations.find((c) => c.id === conversationId)
    const topic = activeConv?.topic ?? 'general'
    try {
      await addDoc(collection(convRef, 'messages'), {
        senderUid: user.uid,
        senderName: user.displayName || user.email || 'Unknown',
        body,
        topic,
        attachments,
        readBy: [user.uid],
        deletedFor: [],
        createdAt: serverTimestamp(),
      })
      await updateDoc(convRef, {
        lastMessage: body.length > 80 ? body.slice(0, 80) + '…' : body,
        lastMessageAt: serverTimestamp(),
        lastSenderUid: user.uid,
        archivedFor: [],
        updatedAt: serverTimestamp(),
        [`participantPhotos.${user.uid}`]: user.photoURL ?? null,
      })
    } catch (err: unknown) {
      console.error('[messaging] sendMessage failed:', err)
      throw err
    }
  },

  markThreadRead: async (conversationId) => {
    const user = currentUser()
    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        [`lastReadAt.${user.uid}`]: serverTimestamp(),
      })
    } catch (err: unknown) {
      console.warn('[messaging] markThreadRead failed:', err)
    }
  },

  softDeleteMessage: async (conversationId, messageId) => {
    const user = currentUser()
    try {
      await updateDoc(doc(db, 'conversations', conversationId, 'messages', messageId), {
        deletedFor: arrayUnion(user.uid),
      })
    } catch (err: unknown) {
      console.error('[messaging] softDeleteMessage failed:', err)
      throw err
    }
  },

  archiveConversation: async (conversationId) => {
    const user = currentUser()
    const convRef = doc(db, 'conversations', conversationId)
    try {
      const snap = await getDoc(convRef)
      if (!snap.exists()) return
      const existing = (snap.data()?.archivedFor ?? []) as string[]
      if (!existing.includes(user.uid)) {
        await updateDoc(convRef, { archivedFor: [...existing, user.uid] })
      }
    } catch (err: unknown) {
      console.warn('[messaging] archiveConversation failed:', err)
    }
  },

  keepThread: async (conversationId) => {
    try {
      await updateDoc(doc(db, 'conversations', conversationId), { keepForever: true })
    } catch (err: unknown) {
      console.error('[messaging] keepThread failed:', err)
      throw err
    }
  },

  downloadThread: (conversationId) => {
    const { conversations, messages } = get()
    const conv = conversations.find((c) => c.id === conversationId)
    if (!conv) return
    const uid = auth.currentUser?.uid
    if (!uid) return
    const names = Object.values(conv.participantNames).join(', ')
    const topic = conv.topic.charAt(0).toUpperCase() + conv.topic.slice(1)
    const lines = [
      `David's Beacon — Conversation Export`,
      `Participants: ${names}`,
      `Topic: ${topic}`,
      `Exported: ${new Date().toLocaleDateString()}`,
      '',
      '────────────────────────────────────',
    ]
    for (const msg of messages) {
      if (msg.deletedFor.includes(uid)) continue
      const ts = msg.createdAt ? new Date(msg.createdAt.toMillis()).toLocaleString() : ''
      lines.push(`[${ts}] ${msg.senderName}`)
      lines.push(msg.body)
      lines.push('')
    }
    lines.push('────────────────────────────────────')
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `conversation-${Date.now()}.txt`
    a.click()
    URL.revokeObjectURL(url)
  },
}))
